/**
 * 仓内作业折线图：拖动圈选区间 + 悬停显示每小时数字。逐行移植产品原型 page.tsx 的 OpsChart（SVG 手绘）。
 *
 * 与原型的差别：横轴小时是 hub 当地时间（原型写死美西），浮层标题相应改为「Hub 当地时间」；文案走 i18n。
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../constants';
import { useFmt } from '../lib/useFmt';
import { I18N } from '../lib/useLabels';

export type Sel = { i0: number; i1: number } | null;

export interface OpsSeries {
    k: string;
    label: string;
    color: string;
    values: number[];
}

interface Props {
    series: OpsSeries[];
    /** hub 当地时间的 'YYYY-MM-DD HH' */
    labels: string[];
    sel: Sel;
    onSelect: (s: Sel) => void;
}

const W = 1000;
const H = 240;
const padL = 56;
const padR = 16;
const padT = 14;
const padB = 30;

const OpsChart: React.FC<Props> = ({ series, labels, sel, onSelect }) => {
    const { t } = useTranslation();
    const fmt = useFmt();
    const n = labels.length;
    const svgRef = React.useRef<SVGSVGElement | null>(null);
    const [hover, setHover] = React.useState<number | null>(null);
    const [drag, setDragState] = React.useState<{ a: number; b: number } | null>(null);
    const dragRef = React.useRef<{ a: number; b: number } | null>(null); // 事件里用引用，避免依赖渲染时序
    const setDrag = (d: { a: number; b: number } | null) => {
        dragRef.current = d;
        setDragState(d);
    };

    const max = Math.max(1, ...series.flatMap((s) => s.values));
    const niceMax = (() => {
        const p = 10 ** Math.floor(Math.log10(max));
        return Math.ceil(max / p) * p;
    })();
    const x = (i: number) => padL + (n <= 1 ? 0 : (i / (n - 1)) * (W - padL - padR));
    const y = (v: number) => padT + (1 - v / niceMax) * (H - padT - padB);
    const ticks = [0, 0.25, 0.5, 0.75, 1];
    const step = n > 30 ? 4 : 3;

    const idxAt = (clientX: number): number => {
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect || n <= 1) return 0;
        const px = ((clientX - rect.left) / rect.width) * W;
        const r = (px - padL) / (W - padL - padR);
        return Math.min(n - 1, Math.max(0, Math.round(r * (n - 1))));
    };
    const finishDrag = () => {
        const d = dragRef.current;
        if (!d) return;
        if (d.a === d.b) onSelect(null);
        else onSelect({ i0: Math.min(d.a, d.b), i1: Math.max(d.a, d.b) });
        setDrag(null);
    };

    const shown = drag ? { i0: Math.min(drag.a, drag.b), i1: Math.max(drag.a, drag.b) } : sel;
    const tipOnLeft = hover !== null && hover > n / 2;

    return (
        <div>
            <div className="mb-1 flex flex-wrap gap-3 text-[12.5px]" style={{ color: C.text2 }}>
                {series.map((s) => (
                    <span key={s.k} className="inline-flex items-center gap-1.5">
                        <span style={{ width: 14, height: 3, background: s.color, display: 'inline-block', borderRadius: 2 }} />
                        {s.label}
                    </span>
                ))}
            </div>
            <div className="relative select-none">
                <svg
                    ref={svgRef}
                    viewBox={`0 0 ${W} ${H}`}
                    width="100%"
                    style={{ display: 'block', cursor: 'crosshair' }}
                    role="img"
                    aria-label={t(`${I18N}.ops.chartAria`) ?? ''}
                    onMouseDown={(e) => {
                        const i = idxAt(e.clientX);
                        setDrag({ a: i, b: i });
                    }}
                    onMouseMove={(e) => {
                        const i = idxAt(e.clientX);
                        setHover(i);
                        if (dragRef.current) setDrag({ a: dragRef.current.a, b: i });
                    }}
                    onMouseUp={finishDrag}
                    onMouseLeave={() => {
                        setHover(null);
                        finishDrag();
                    }}
                >
                    {ticks.map((tk) => (
                        <g key={tk}>
                            <line x1={padL} x2={W - padR} y1={y(niceMax * tk)} y2={y(niceMax * tk)} stroke={C.borderLight} />
                            <text x={padL - 8} y={y(niceMax * tk) + 4} textAnchor="end" fontSize="12" fill={C.text3}>
                                {fmt.pcs(Math.round(niceMax * tk))}
                            </text>
                        </g>
                    ))}
                    {shown && (
                        <rect
                            x={x(shown.i0)}
                            y={padT}
                            width={Math.max(2, x(shown.i1) - x(shown.i0))}
                            height={H - padT - padB}
                            fill="rgba(114,46,209,.10)"
                            stroke={C.primary}
                            strokeWidth="1"
                            strokeDasharray="4 3"
                        />
                    )}
                    {labels.map((l, i) =>
                        i % step === 0 || i === n - 1 ? (
                            <text key={l} x={x(i)} y={H - 10} textAnchor="middle" fontSize="12" fill={C.text3}>
                                {fmt.hourText(l)}
                            </text>
                        ) : null
                    )}
                    {series.map((s) => (
                        <polyline
                            key={s.k}
                            fill="none"
                            stroke={s.color}
                            strokeWidth="2"
                            strokeLinejoin="round"
                            points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
                        />
                    ))}
                    {hover !== null && (
                        <g pointerEvents="none">
                            <line x1={x(hover)} x2={x(hover)} y1={padT} y2={H - padB} stroke={C.text3} strokeDasharray="3 3" />
                            {series.map((s) => (
                                <circle
                                    key={s.k}
                                    cx={x(hover)}
                                    cy={y(s.values[hover])}
                                    r="4"
                                    fill="#fff"
                                    stroke={s.color}
                                    strokeWidth="2"
                                />
                            ))}
                        </g>
                    )}
                </svg>
                {hover !== null && (
                    <div
                        className="pointer-events-none absolute z-10 rounded-md bg-white px-3 py-2 text-[12.5px] shadow-lg"
                        style={{
                            top: 8,
                            border: `1px solid ${C.border}`,
                            ...(tipOnLeft
                                ? { right: `${100 - (x(hover) / W) * 100 + 1}%` }
                                : { left: `${(x(hover) / W) * 100 + 1}%` }),
                            minWidth: 150
                        }}
                    >
                        <div className="mb-1 font-semibold" style={{ color: C.text1 }}>
                            {t(`${I18N}.ops.tipTitle`, { hour: fmt.hourText(labels[hover]) })}
                        </div>
                        {series.map((s) => (
                            <div key={s.k} className="flex items-center justify-between gap-4" style={{ color: C.text2 }}>
                                <span className="inline-flex items-center gap-1.5">
                                    <span
                                        style={{ width: 8, height: 8, borderRadius: 2, background: s.color, display: 'inline-block' }}
                                    />
                                    {s.label}
                                </span>
                                <span className="tabular-nums" style={{ color: C.text1 }}>
                                    {fmt.num(s.values[hover])}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OpsChart;
